import React, { useEffect, useState } from 'react';
import axios from 'axios';

// ─────────────────────────────────────────────
// 실거래 조회 설정 (관리탭 > 관리자 전용)
//
//  · 단지 주변 중개사무소를 집계할 반경(m)을 설정한다.
//  · 값은 app_config 테이블의 rt_agent_radius_m 에 저장된다.
//    (slider_config 와 동일한 방식 - 소스 수정 없이 값만 바꾸면 됨)
//
// [연관]
//  - 백엔드 GET/POST /api/real-trades/config
//  - 단지분석 탭의 "주변 중개사무소 표시" 가 이 값을 사용
// ─────────────────────────────────────────────

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// 자주 쓰는 값을 버튼으로 (직접 입력도 가능)
const PRESETS = [
  { v: 200,  t: '200m',  d: '바로 앞' },
  { v: 300,  t: '300m',  d: '단지 인접' },
  { v: 500,  t: '500m',  d: '도보 7분' },
  { v: 1000, t: '1km',   d: '도보 15분' },
];

export default function RealTradeConfig({ cardStyle, titleStyle, subTitleStyle }) {
  const [radius, setRadius] = useState('');
  const [saved, setSaved]   = useState(null);   // 저장된 현재값
  const [msg, setMsg]       = useState('');
  const [loading, setLoading] = useState(false);

  // 현재 설정값 로드
  useEffect(() => {
    axios.get('/api/real-trades/config', auth())
      .then(r => { setRadius(String(r.data.radius_m)); setSaved(r.data.radius_m); })
      .catch(e => setMsg('설정을 불러오지 못했습니다: ' + (e.response?.data?.error || e.message)));
  }, []);

  const save = async (value) => {
    const v = Number(value ?? radius);
    if (!Number.isFinite(v) || v < 50 || v > 5000) {
      setMsg('⚠️ 50 ~ 5000 사이의 숫자를 입력하세요');
      return;
    }
    setLoading(true);
    setMsg('');
    try {
      const res = await axios.post('/api/real-trades/config', { radius_m: v }, auth());
      setSaved(res.data.radius_m);
      setRadius(String(res.data.radius_m));
      setMsg(`✅ ${res.data.radius_m}m 로 저장되었습니다`);
    } catch (e) {
      setMsg('❌ 저장 실패: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={cardStyle}>
      <div style={titleStyle}>📊 실거래 조회 설정</div>
      <div style={subTitleStyle}>
        단지 주변 중개사무소를 집계할 반경을 설정합니다
      </div>

      {/* 현재 설정값 */}
      <div style={{ padding: '10px 14px', background: '#f1f5f9',
                    borderRadius: 6, marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: '#64748b' }}>현재 설정</span>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#1e40af' }}>
          {saved != null ? `${saved.toLocaleString()}m` : '불러오는 중…'}
        </div>
      </div>

      {/* 빠른 선택 */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 6 }}>
          빠른 선택
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {PRESETS.map(p => (
            <button key={p.v} onClick={() => save(p.v)} disabled={loading}
              style={{
                padding: '8px 14px', cursor: 'pointer', borderRadius: 6,
                border: '1px solid ' + (saved === p.v ? '#3b82f6' : '#cbd5e1'),
                background: saved === p.v ? '#dbeafe' : '#fff',
                fontWeight: saved === p.v ? 700 : 400,
                textAlign: 'left', lineHeight: 1.3,
              }}>
              <div style={{ fontSize: 14 }}>{p.t}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>{p.d}</div>
            </button>
          ))}
        </div>
      </div>

      {/* 직접 입력 */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>직접 입력</span>
        <input
          value={radius}
          onChange={e => setRadius(e.target.value.replace(/[^0-9]/g, ''))}
          onKeyDown={e => e.key === 'Enter' && save()}
          style={{ width: 90, padding: '8px', border: '1px solid #cbd5e1',
                   borderRadius: 4, fontSize: 14, textAlign: 'right' }} />
        <span style={{ fontSize: 13, color: '#64748b' }}>m</span>
        <button onClick={() => save()} disabled={loading}
          style={{ padding: '8px 18px', background: '#3b82f6', color: '#fff',
                   border: 'none', borderRadius: 4, cursor: 'pointer',
                   fontSize: 13, fontWeight: 600 }}>
          {loading ? '저장 중…' : '저장'}
        </button>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>50 ~ 5,000m</span>
      </div>

      {msg && (
        <div style={{ marginTop: 10, fontSize: 13,
                      color: msg.startsWith('✅') ? '#166534' : '#b91c1c' }}>
          {msg}
        </div>
      )}

      <p style={{ margin: '12px 0 0', fontSize: 11, color: '#94a3b8', lineHeight: 1.6 }}>
        ※ 반경이 넓을수록 중개사무소가 많이 잡혀 "1곳당 세대수"가 작아집니다.
        도심 상권은 300m, 외곽 단지는 1km 정도가 실제 생활권에 가깝습니다.<br/>
        ※ 변경 즉시 적용됩니다. 단지분석 탭에서 "주변 중개사무소 표시"를 다시 누르면
        새 반경으로 계산됩니다.
      </p>
    </div>
  );
}